/* eslint-disable react-doctor/no-prop-callback-in-effect -- onSectionToggle notifies parent of layout changes; lifting the accordion state would require significant API changes */
/* eslint-disable react-doctor/prefer-use-effect-event -- useEffectEvent is not stable in React 19; close is a stable useCallback so there is no actual re-subscription issue */
'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { ChartLegend } from '@/components/map/ChartLegend';
import { SearchFilter } from '@/components/filters/SearchFilter';
import { RangeFilter } from '@/components/filters/RangeFilter';
import { TargetAudienceFilter } from '@/components/filters/TargetAudienceFilter';
import { OnSaleFilter } from '@/components/filters/OnSaleFilter';
import { CountryFilter } from '@/components/filters/CountryFilter';
import { ClearFiltersButton } from '@/components/filters/ClearFiltersButton';
import { ActiveFilters } from '@/components/filters/ActiveFilters';
import type { LegendEntry } from '@/components/map/MapChart';

interface Props {
  entries: LegendEntry[];
  hiddenSeries: Set<string>;
  onToggle: (name: string) => void;
  onZoom: (name: string) => void;
  onSelectAll: () => void;
  onHideAll: () => void;
  filteredCount: number;
  totalCount: number;
  onSectionToggle?: () => void;
}

const Chevron: React.FC<{ open: boolean }> = ({ open }) =>
  open
    ? <ChevronDown size={12} className="text-text-muted" aria-hidden="true" />
    : <ChevronRight size={12} className="text-text-muted" aria-hidden="true" />;

export const Sidebar: React.FC<Props> = ({
  entries, hiddenSeries, onToggle, onZoom, onSelectAll, onHideAll, filteredCount, totalCount, onSectionToggle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(true);

  useEffect(() => {
    onSectionToggle?.();
  }, [filtersOpen, legendOpen, onSectionToggle]);
  const sheetRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const open = useCallback((): void => setIsOpen(true), []);
  const close = useCallback((): void => setIsOpen(false), []);

  // Escape to close bottom sheet
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, close]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      const focusable = sheetRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    } else {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;
    const trap = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;
      const els = Array.from(sheetRef.current!.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ));
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [isOpen]);

  const isFiltered = totalCount > 0 && filteredCount !== totalCount;
  const hiddenCount = hiddenSeries.size;

  const sidebarContent = (
    <>
      {/* Filters section */}
      <div className="flex-shrink-0 border-b border-border">
        {/* Header row — toggle is just the label+chevron; count+clear are separate actions */}
        <div className="flex items-center justify-between px-[14px] py-[10px]">
          <button
            type="button"
            onClick={() => setFiltersOpen(v => !v)}
            aria-expanded={filtersOpen}
            className="flex items-center gap-[6px] cursor-pointer group p-1 -ml-1 rounded-sm"
          >
            <span className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted group-hover:text-text-secondary transition-colors">
              Filters
            </span>
            <Chevron open={filtersOpen} />
          </button>
          <div className="flex items-center gap-[8px]">
            {isFiltered && (
              <span className="text-[10px] text-text-secondary tabular-nums">
                {filteredCount.toLocaleString()}<span className="text-text-muted">/{totalCount.toLocaleString()}</span>
              </span>
            )}
            <ClearFiltersButton />
          </div>
        </div>

        {filtersOpen && (
          <div className={`px-[14px] pb-[10px] flex flex-col gap-[10px] overflow-y-auto ${legendOpen ? 'max-h-[45dvh]' : 'max-h-[60dvh]'}`}>
            <ActiveFilters />
            <SearchFilter />
            <RangeFilter filterKey="mrrRange" label="MRR (USD)" minDefault={0} maxDefault={Infinity} />
            <CountryFilter />
            <TargetAudienceFilter />
            <OnSaleFilter />
            <RangeFilter
              filterKey="growthRange"
              label="MRR Growth (%)"
              minDefault={-Infinity}
              maxDefault={Infinity}
              placeholder={['Min %', 'Max %']}
              allowNegativeMin
            />
            <RangeFilter filterKey="customersRange" label="Customers" minDefault={0} maxDefault={Infinity} />
          </div>
        )}
      </div>

      {/* Clusters section */}
      <div className="flex flex-col flex-1 min-h-0">
        {/* Header row */}
        <button
          type="button"
          onClick={() => setLegendOpen(v => !v)}
          aria-expanded={legendOpen}
          className="flex items-center justify-between px-[14px] py-[10px] border-b border-border flex-shrink-0 cursor-pointer group"
        >
          <div className="flex items-center gap-[6px]">
            <span className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted group-hover:text-text-secondary transition-colors">
              Clusters
            </span>
            <Chevron open={legendOpen} />
          </div>
          {hiddenCount > 0 && entries.length > 0 && (
            <span className="text-[10px] text-text-secondary tabular-nums">
              {entries.length - hiddenCount}<span className="text-text-muted">/{entries.length}</span>
            </span>
          )}
        </button>

        {legendOpen && (
          <>
            <div className="flex gap-[6px] px-[14px] py-[10px] border-b border-border bg-bg-panel flex-shrink-0">
              <button
                type="button"
                onClick={onSelectAll}
                className="text-[11px] font-medium px-[10px] py-[3px] rounded-sm border border-accent text-accent cursor-pointer transition-colors hover:bg-accent-dim tracking-[0.03em]"
                aria-label="Show all clusters"
              >
                All
              </button>
              <button
                type="button"
                onClick={onHideAll}
                className="text-[11px] font-medium px-[10px] py-[3px] rounded-sm border border-border-mid text-text-muted cursor-pointer transition-colors hover:bg-bg-raised hover:border-border hover:text-text-secondary tracking-[0.03em]"
                aria-label="Hide all clusters"
              >
                None
              </button>
            </div>
            <ChartLegend entries={entries} hiddenSeries={hiddenSeries} onToggle={onToggle} onZoom={onZoom} />
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden desk:flex flex-col flex-shrink-0 w-sidebar bg-[var(--bg-panel-translucent)] border-r border-border-sidebar overflow-hidden"
        aria-label="Filters and clusters"
      >
        {sidebarContent}
      </aside>

      {/* Mobile: pill trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={open}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Open filters and clusters"
        className="desk:hidden fixed bottom-5 left-1/2 -translate-x-1/2 z-[98] inline-flex items-center gap-[6px] bg-bg-panel border border-border-mid text-text-secondary text-[11px] font-medium px-5 py-2 rounded-[20px] tracking-[0.05em] whitespace-nowrap transition-colors hover:bg-bg-raised hover:border-border"
      >
        <ChevronUp size={14} aria-hidden="true" /> Filters & Clusters
      </button>

      {/* Mobile: backdrop */}
      {isOpen && (
        <div
          className="desk:hidden fixed inset-0 bg-bg/55 z-[99]"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Mobile: bottom sheet */}
      <aside
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Filters and clusters"
        className={`desk:hidden fixed bottom-0 left-0 right-0 h-[72dvh] z-[100] flex flex-col bg-[var(--bg-panel-translucent)] border-t border-border rounded-tl-[14px] rounded-tr-[14px] sidebar-sheet ${isOpen ? 'sheet-open' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="w-9 h-1 bg-border-mid rounded-sm mx-auto mt-[10px] flex-shrink-0" aria-hidden="true" />
        {sidebarContent}
      </aside>
    </>
  );
};
